
import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface RecipeFeedbackProps {
  conversionResult: any;
  originalInput: {
    url?: string | null;
    text?: string | null;
  };
}

const RecipeFeedback = ({ conversionResult, originalInput }: RecipeFeedbackProps) => {
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedback = async (type: 'positive' | 'negative') => {
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      const feedbackData = {
        user_id: user?.id,
        original_url: originalInput.url,
        original_text: originalInput.text,
        conversion_result: conversionResult,
        feedback_type: type,
        feedback: type === 'negative' ? feedback : ''
      };

      const { error } = await supabase
        .from('recipe_conversions')
        .insert(feedbackData);

      if (error) throw error;

      toast.success(
        type === 'positive' 
          ? "Thank you for your positive feedback!" 
          : "Thank you for helping us improve!"
      );
      
      setShowFeedbackInput(false);
      setFeedback("");
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-8 max-w-3xl mx-auto">
      <Alert className="bg-white/80 backdrop-blur-sm">
        <AlertDescription className="text-center py-2">
          This recipe was converted using AI. While we strive for accuracy, review the instructions. 
          Your feedback helps improve our conversion system for everyone in the community!
        </AlertDescription>
      </Alert>
      
      <div className="mt-4 space-y-4">
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            onClick={() => handleFeedback('positive')}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <ThumbsUp className="w-4 h-4" />
            Looks Good
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFeedbackInput(true)}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <ThumbsDown className="w-4 h-4" />
            Needs Improvement
          </Button>
        </div>

        {showFeedbackInput && (
          <div className="space-y-4">
            <Textarea
              placeholder="Please let us know what needs improvement..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              className="w-full"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFeedbackInput(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleFeedback('negative')}
                disabled={isSubmitting || !feedback.trim()}
              >
                Submit Feedback
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipeFeedback;
